import { useEffect, useRef, useState } from 'react'
import {
  Area,
  AreaChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts'

interface RealtimeDataPoint {
  time: string
  value: number
}

interface RealtimeChartProps {
  title: string
  color: string
  maxPoints?: number
  updateInterval?: number
}

export function RealtimeChart({
  title,
  color = '#6366f1',
  maxPoints = 20,
  updateInterval = 5000,
}: RealtimeChartProps) {
  const [data, setData] = useState<RealtimeDataPoint[]>([])
  const intervalRef = useRef<NodeJS.Timeout>()

  useEffect(() => {
    // Initialize with some data
    const initialData: RealtimeDataPoint[] = []
    const now = Date.now()
    for (let i = maxPoints - 1; i >= 0; i--) {
      initialData.push({
        time: new Date(now - i * updateInterval).toLocaleTimeString(),
        value: Math.floor(Math.random() * 100) + 20,
      })
    }
    setData(initialData)

    // Update data at intervals
    intervalRef.current = setInterval(() => {
      setData((prevData) => {
        const newData = [...prevData]
        if (newData.length >= maxPoints) {
          newData.shift()
        }
        newData.push({
          time: new Date().toLocaleTimeString(),
          value: Math.floor(Math.random() * 100) + 20,
        })
        return newData
      })
    }, updateInterval)

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current)
      }
    }
  }, [maxPoints, updateInterval])

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-gray-900">{title}</h3>
        <div className="flex items-center">
          <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse mr-2" />
          <span className="text-sm text-gray-500">Live</span>
        </div>
      </div>
      <ResponsiveContainer width="100%" height={200}>
        <AreaChart data={data}>
          <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
          <XAxis dataKey="time" stroke="#9ca3af" fontSize={10} interval="preserveStartEnd" />
          <YAxis stroke="#9ca3af" fontSize={12} />
          <Tooltip />
          <Area
            type="monotone"
            dataKey="value"
            stroke={color}
            fill={color}
            fillOpacity={0.1}
            strokeWidth={2}
            animationDuration={300}
          />
        </AreaChart>
      </ResponsiveContainer>
    </div>
  )
}
